/**
 * MACIRI DONIA — THE DONIA STAGE
 * Editorial presentation carousel. Reuses the focus-and-reveal motion:
 * centered frame, adjacent peeks, drag/swipe, arrows, slow autoplay.
 */
(function () {
  'use strict';

  var SWIPE = 48;
  var DRAG = 8;

  function parseBool(v, d) {
    return v === undefined || v === '' ? d : v !== 'false';
  }

  function clamp(v, min, max) {
    return Math.min(max, Math.max(min, v));
  }

  function pad2(n) {
    return (n < 10 ? '0' : '') + String(n);
  }

  function HeroCarousel(root) {
    this.root = root;
    this.viewport = root.querySelector('[data-hero-viewport]');
    this.track = root.querySelector('[data-hero-track]');
    if (!this.viewport || !this.track) return;

    this.prevBtns = root.querySelectorAll('[data-hero-prev]');
    this.nextBtns = root.querySelectorAll('[data-hero-next]');
    this.dotsEl = root.querySelector('[data-hero-dots]');
    this.counterEl = root.querySelector('[data-hero-counter]');
    this.currentEl = root.querySelector('[data-hero-current]');
    this.totalEl = root.querySelector('[data-hero-total]');

    this.cfg = {
      autoplay: parseBool(root.dataset.autoplay, true),
      speed: parseFloat(root.dataset.carouselSpeed) || 28,
      pauseMs: (parseFloat(root.dataset.pauseDuration) || 6) * 1000,
      zoom: (parseFloat(root.dataset.zoomAmount) || 4) / 100 + 1,
      zoomMobile: (parseFloat(root.dataset.zoomAmountMobile) || 3) / 100 + 1,
      transMs: parseFloat(root.dataset.transitionSpeed) || 900,
      reveal: parseBool(root.dataset.revealEnabled, true),
      resumeMs: (parseFloat(root.dataset.resumeDelay) || 8) * 1000
    };

    this.realSlides = Array.prototype.filter.call(
      this.track.querySelectorAll('[data-hero-slide]'),
      function (el) {
        return !el.hasAttribute('data-hero-clone');
      }
    );

    if (!this.realSlides.length) return;

    this.count = this.realSlides.length;
    this.slides = [];
    this.baseOffset = [];
    this.offset = 0;
    this.index = 0;
    this.state = 'idle';
    this.timers = [];
    this.raf = null;
    this.resumeTimer = null;
    this.userPaused = false;
    this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.destroyed = false;
    this.drag = { active: false, x: 0, startX: 0, startOffset: 0, moved: false };

    root.style.setProperty('--eh-transition', this.cfg.transMs + 'ms');
    root.style.setProperty('--eh-zoom', String(this.cfg.zoom));
    root.style.setProperty('--eh-zoom-mobile', String(this.cfg.zoomMobile));

    this.setupClones();
    this.buildDots();
    this.updateIndicator();
    this.bind();
    this.ready();
  }

  HeroCarousel.prototype.kill = function () {
    this.destroyed = true;
    this.clearTimers();
    if (this.resumeTimer) clearTimeout(this.resumeTimer);
    if (this.raf) cancelAnimationFrame(this.raf);
    this.pauseAllVideo();
  };

  HeroCarousel.prototype.clearTimers = function () {
    this.timers.forEach(clearTimeout);
    this.timers = [];
  };

  HeroCarousel.prototype.setupClones = function () {
    this.slides = Array.prototype.slice.call(this.realSlides);
    if (this.count < 2) return;

    var first = this.realSlides[0].cloneNode(true);
    var last = this.realSlides[this.count - 1].cloneNode(true);
    first.setAttribute('data-hero-clone', 'first');
    last.setAttribute('data-hero-clone', 'last');
    first.setAttribute('aria-hidden', 'true');
    last.setAttribute('aria-hidden', 'true');

    var clones = [first, last];
    for (var c = 0; c < clones.length; c++) {
      var focusable = clones[c].querySelectorAll('a, button, video, [tabindex]');
      Array.prototype.forEach.call(focusable, function (el) {
        el.setAttribute('tabindex', '-1');
      });
    }

    this.track.insertBefore(last, this.realSlides[0]);
    this.track.appendChild(first);
    this.slides = Array.prototype.slice.call(this.track.querySelectorAll('[data-hero-slide]'));
  };

  HeroCarousel.prototype.domFor = function (i) {
    return this.count < 2 ? 0 : i + 1;
  };

  HeroCarousel.prototype.layout = function () {
    this.offset = 0;
    this.setTransform(false);

    var vp = this.viewport.clientWidth;
    if (!vp) return false;

    this.baseOffset = this.slides.map(function (slide) {
      if (!slide.offsetWidth) return 0;
      var center = slide.offsetLeft + slide.offsetWidth / 2;
      return vp / 2 - center;
    });

    return this.slides.some(function (slide) {
      return slide.offsetWidth > 0;
    });
  };

  HeroCarousel.prototype.targetOffset = function (realIndex) {
    var dom = this.domFor(realIndex);
    return this.baseOffset[dom] || 0;
  };

  HeroCarousel.prototype.setTransform = function (animate) {
    this.track.style.transition = animate
      ? 'transform ' + this.cfg.transMs + 'ms cubic-bezier(0.22, 1, 0.36, 1)'
      : 'none';
    this.track.style.transform = 'translate3d(' + this.offset + 'px, 0, 0)';
    if (!animate) void this.track.offsetWidth;
  };

  HeroCarousel.prototype.applyStates = function () {
    if (this.state === 'feature') return;

    var vp = this.viewport.getBoundingClientRect();
    var cx = vp.left + vp.width / 2;
    var featuredBand = Math.max(48, vp.width * 0.12);
    var nearBand = vp.width * 0.42;

    this.slides.forEach(function (slide) {
      slide.classList.remove('is-featured', 'is-near-center');
      var r = slide.getBoundingClientRect();
      var d = Math.abs(r.left + r.width / 2 - cx);
      if (d < featuredBand) slide.classList.add('is-featured');
      else if (d < nearBand) slide.classList.add('is-near-center');
    });
  };

  HeroCarousel.prototype.setImage = function (slide, idx) {
    var imgs = slide.querySelectorAll('[data-hero-img]');
    if (imgs.length < 2) return;
    Array.prototype.forEach.call(imgs, function (img, i) {
      img.classList.toggle('is-active', i === idx);
    });
  };

  HeroCarousel.prototype.resetImages = function (slide) {
    this.setImage(slide, 0);
  };

  HeroCarousel.prototype.videosIn = function (slide) {
    return slide ? slide.querySelectorAll('[data-hero-video]') : [];
  };

  HeroCarousel.prototype.pauseAllVideo = function () {
    var videos = this.root.querySelectorAll('[data-hero-video]');
    Array.prototype.forEach.call(videos, function (video) {
      try {
        video.pause();
      } catch (e) {}
    });
    this.slides.forEach(function (slide) {
      slide.classList.remove('is-playing');
    });
  };

  HeroCarousel.prototype.syncVideo = function (realIndex) {
    var self = this;
    this.slides.forEach(function (slide, i) {
      var featured = i === self.domFor(realIndex) && !slide.hasAttribute('data-hero-clone');
      var videos = self.videosIn(slide);
      Array.prototype.forEach.call(videos, function (video) {
        if (featured && slide.getAttribute('data-video-autoplay') === 'true' && !self.userPaused) {
          video.muted = true;
          var play = video.play();
          if (play && play.catch) play.catch(function () {});
          slide.classList.add('is-playing');
        } else if (!featured) {
          try {
            video.pause();
            if (video.currentTime > 0.2) video.currentTime = 0;
          } catch (e) {}
          slide.classList.remove('is-playing');
        }
      });
    });
  };

  HeroCarousel.prototype.playVideo = function (slide, unmute) {
    if (!slide) return;
    var video = slide.querySelector('[data-hero-video]');
    if (!video) return;
    if (unmute) video.muted = false;
    var play = video.play();
    if (play && play.catch) play.catch(function () {});
    slide.classList.add('is-playing');
  };

  HeroCarousel.prototype.updateIndicator = function () {
    if (this.currentEl) this.currentEl.textContent = pad2(this.index + 1);
    if (this.totalEl) this.totalEl.textContent = pad2(this.count);
    if (this.counterEl) {
      this.counterEl.setAttribute(
        'aria-label',
        'Presentation ' + (this.index + 1) + ' of ' + this.count
      );
    }
  };

  HeroCarousel.prototype.feature = function (realIndex) {
    var self = this;
    this.clearTimers();
    this.state = 'feature';
    this.index = realIndex;
    var slide = this.slides[this.domFor(realIndex)];

    this.slides.forEach(function (s) {
      s.classList.remove('is-featured');
      self.resetImages(s);
    });

    if (!slide) return;

    slide.classList.add('is-featured');
    this.setImage(slide, 0);
    this.updateDots();
    this.updateIndicator();
    this.syncVideo(realIndex);

    var imgs = slide.querySelectorAll('[data-hero-img]');
    var half = this.cfg.pauseMs / 2;
    var t = this.reduced ? 0 : this.cfg.transMs;
    var canReveal =
      this.cfg.reveal &&
      imgs.length > 1 &&
      !slide.querySelector('[data-hero-video]');

    if (canReveal && !this.reduced) {
      this.timers.push(
        setTimeout(function () {
          self.setImage(slide, 1);
        }, t + half)
      );
    }

    if (this.cfg.autoplay && !this.userPaused && !this.reduced && this.count > 1) {
      this.timers.push(
        setTimeout(function () {
          self.unfeature();
        }, t + this.cfg.pauseMs)
      );
    }
  };

  HeroCarousel.prototype.unfeature = function () {
    var self = this;
    var slide = this.slides[this.domFor(this.index)];
    var t = this.reduced ? 0 : this.cfg.transMs;

    if (slide) {
      slide.classList.remove('is-featured');
      this.resetImages(slide);
    }

    this.pauseAllVideo();

    if (this.userPaused || !this.cfg.autoplay || this.reduced || this.count < 2) {
      this.state = 'idle';
      return;
    }

    var next = (this.index + 1) % this.count;
    this.timers.push(
      setTimeout(function () {
        self.go(next, true);
      }, t)
    );
  };

  HeroCarousel.prototype.snap = function (realIndex) {
    this.offset = this.targetOffset(realIndex);
    this.index = realIndex;
    this.setTransform(false);
    this.applyStates();
    this.updateIndicator();
  };

  HeroCarousel.prototype.moveOffset = function (to, done) {
    var self = this;
    var from = this.offset;
    var dist = to - from;
    var pxPerSec = Math.max((this.cfg.speed || 45) * 12, 200);
    var dur = this.reduced ? 0 : clamp(Math.abs(dist) / pxPerSec, 0.35, 1.5) * 1000;
    var start = null;

    this.state = 'move';
    this.pauseAllVideo();

    function frame(ts) {
      if (self.destroyed) return;
      if (!start) start = ts;
      var p = dur === 0 ? 1 : clamp((ts - start) / dur, 0, 1);
      var ease = 1 - Math.pow(1 - p, 3);
      self.offset = from + dist * ease;
      self.setTransform(false);
      self.applyStates();

      if (p < 1) {
        self.raf = requestAnimationFrame(frame);
      } else {
        self.offset = to;
        self.setTransform(false);
        self.applyStates();
        self.raf = null;
        self.state = 'idle';
        if (done) done();
      }
    }

    if (this.raf) cancelAnimationFrame(this.raf);
    this.raf = requestAnimationFrame(frame);
  };

  HeroCarousel.prototype.moveTo = function (realIndex, done) {
    this.moveOffset(this.targetOffset(realIndex), done);
  };

  HeroCarousel.prototype.start = function () {
    if (this.state === 'move') return;
    this.snap(this.index);
    this.feature(this.index);
  };

  HeroCarousel.prototype.stopAutoplay = function () {
    var self = this;
    this.userPaused = true;
    this.clearTimers();
    if (this.resumeTimer) clearTimeout(this.resumeTimer);
    this.resumeTimer = setTimeout(function () {
      if (self.destroyed) return;
      self.userPaused = false;
      if (self.cfg.autoplay && self.state !== 'move') self.start();
    }, this.cfg.resumeMs);
  };

  HeroCarousel.prototype.go = function (realIndex, animate) {
    var self = this;
    this.stopAutoplay();
    if (this.raf) {
      cancelAnimationFrame(this.raf);
      this.raf = null;
    }

    realIndex = ((realIndex % this.count) + this.count) % this.count;

    if (!this.baseOffset || this.baseOffset.length !== this.slides.length) {
      this.layout();
    }

    if (!animate || this.count < 2 || this.reduced) {
      this.snap(realIndex);
      this.feature(realIndex);
      return;
    }

    var from = this.index;
    var last = this.count - 1;
    var wrapForward = from === last && realIndex === 0;
    var wrapBack = from === 0 && realIndex === last;

    function after() {
      self.snap(realIndex);
      self.feature(realIndex);
    }

    if (wrapForward || wrapBack) {
      var cloneDom = wrapForward ? this.slides.length - 1 : 0;
      var to = this.baseOffset[cloneDom];
      if (typeof to !== 'number') to = 0;
      this.moveOffset(to, after);
    } else {
      this.moveTo(realIndex, after);
    }
  };

  HeroCarousel.prototype.pauseUser = function () {
    this.stopAutoplay();
    if (this.raf) {
      cancelAnimationFrame(this.raf);
      this.raf = null;
    }
    if (this.state === 'move') this.state = 'idle';
  };

  HeroCarousel.prototype.nearestIndex = function () {
    var vp = this.viewport.getBoundingClientRect();
    var cx = vp.left + vp.width / 2;
    var best = 0;
    var bestD = Infinity;

    for (var i = 0; i < this.slides.length; i++) {
      var slide = this.slides[i];
      if (!slide) continue;
      var r = slide.getBoundingClientRect();
      var d = Math.abs(r.left + r.width / 2 - cx);
      if (d < bestD) {
        bestD = d;
        best = i;
      }
    }

    var closest = this.slides[best];
    if (closest && closest.getAttribute('data-hero-clone') === 'first') return 0;
    if (closest && closest.getAttribute('data-hero-clone') === 'last') return this.count - 1;
    return this.count < 2 ? 0 : best - 1;
  };

  HeroCarousel.prototype.buildDots = function () {
    if (!this.dotsEl || this.count < 2) return;
    this.dotsEl.innerHTML = '';

    for (var i = 0; i < this.count; i++) {
      var dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'editorial-hero__dot' + (i === 0 ? ' is-active' : '');
      dot.setAttribute('data-hero-to', String(i));
      dot.setAttribute('aria-label', 'Go to presentation ' + (i + 1));
      this.dotsEl.appendChild(dot);
    }
  };

  HeroCarousel.prototype.updateDots = function () {
    if (!this.dotsEl) return;
    var dots = this.dotsEl.querySelectorAll('.editorial-hero__dot');
    var self = this;
    Array.prototype.forEach.call(dots, function (dot, i) {
      dot.classList.toggle('is-active', i === self.index);
    });
  };

  HeroCarousel.prototype.isInteractive = function (target) {
    return !!(
      target &&
      target.closest &&
      target.closest('[data-hero-play], [data-hero-prev], [data-hero-next], [data-notify], .editorial-hero__slide-cta, button, a, input')
    );
  };

  HeroCarousel.prototype.bind = function () {
    var self = this;

    this.root.addEventListener(
      'click',
      function (e) {
        var prev = e.target.closest('[data-hero-prev]');
        if (prev && self.root.contains(prev)) {
          e.preventDefault();
          e.stopPropagation();
          self.go(self.index - 1, true);
          return;
        }

        var next = e.target.closest('[data-hero-next]');
        if (next && self.root.contains(next)) {
          e.preventDefault();
          e.stopPropagation();
          self.go(self.index + 1, true);
          return;
        }

        var dot = e.target.closest('[data-hero-to]');
        if (dot && self.root.contains(dot)) {
          var idx = parseInt(dot.getAttribute('data-hero-to'), 10);
          if (!isNaN(idx)) {
            e.preventDefault();
            e.stopPropagation();
            self.go(idx, true);
          }
          return;
        }

        var play = e.target.closest('[data-hero-play]');
        if (!play || !self.root.contains(play)) return;
        e.preventDefault();
        e.stopPropagation();
        var slide = play.closest('[data-hero-slide]');
        self.playVideo(slide, true);
        self.pauseUser();
      },
      true
    );

    this.root.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        self.go(self.index - 1, true);
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        self.go(self.index + 1, true);
      }
    });

    function down(x) {
      self.pauseUser();
      self.drag.active = true;
      self.drag.moved = false;
      self.drag.startX = x;
      self.drag.startOffset = self.offset;
      self.viewport.classList.add('is-dragging');
      self.clearTimers();
      if (self.raf) cancelAnimationFrame(self.raf);
    }

    function move(x) {
      if (!self.drag.active) return;
      var delta = x - self.drag.startX;
      if (Math.abs(delta) > DRAG) self.drag.moved = true;
      self.offset = self.drag.startOffset + delta;
      self.setTransform(false);
      self.applyStates();
    }

    function up() {
      if (!self.drag.active) return;
      self.drag.active = false;
      self.viewport.classList.remove('is-dragging');
      var next = self.nearestIndex();
      var delta = self.offset - self.drag.startOffset;
      if (self.drag.moved && Math.abs(delta) > SWIPE) {
        if (delta < 0) next = self.index + 1;
        else next = self.index - 1;
      }
      self.go(next, true);
    }

    this.viewport.addEventListener(
      'touchstart',
      function (e) {
        if (self.isInteractive(e.target)) return;
        down(e.touches[0].clientX);
      },
      { passive: true }
    );

    this.viewport.addEventListener(
      'touchmove',
      function (e) {
        move(e.touches[0].clientX);
      },
      { passive: true }
    );

    this.viewport.addEventListener('touchend', up, { passive: true });
    this.viewport.addEventListener('touchcancel', up, { passive: true });

    this.viewport.addEventListener('mousedown', function (e) {
      if (e.button !== 0) return;
      if (self.isInteractive(e.target)) return;
      e.preventDefault();
      down(e.clientX);

      function mm(ev) {
        move(ev.clientX);
      }
      function mu() {
        up();
        document.removeEventListener('mousemove', mm);
        document.removeEventListener('mouseup', mu);
      }

      document.addEventListener('mousemove', mm);
      document.addEventListener('mouseup', mu);
    });

    this.viewport.addEventListener(
      'click',
      function (e) {
        if (self.drag.moved) {
          e.preventDefault();
          e.stopPropagation();
        }
      },
      true
    );

    window.addEventListener('resize', function () {
      clearTimeout(self.resizeTimer);
      self.resizeTimer = setTimeout(function () {
        if (self.destroyed) return;
        self.layout();
        self.snap(self.index);
        self.feature(self.index);
      }, 150);
    });
  };

  HeroCarousel.prototype.boot = function () {
    var self = this;
    var ok = this.layout();

    if (!ok) {
      this.bootAttempts = (this.bootAttempts || 0) + 1;
      if (this.bootAttempts < 20) {
        setTimeout(function () {
          self.boot();
        }, 100);
      }
      return;
    }

    this.offset = this.targetOffset(0);
    this.index = 0;
    this.setTransform(false);

    if (this.cfg.autoplay && !this.reduced) {
      this.start();
    } else {
      this.feature(0);
    }
  };

  HeroCarousel.prototype.ready = function () {
    var self = this;
    var imgs = this.track.querySelectorAll('img');
    var waits = [];

    Array.prototype.forEach.call(imgs, function (img) {
      if (!img.complete) {
        waits.push(
          new Promise(function (resolve) {
            img.addEventListener('load', resolve, { once: true });
            img.addEventListener('error', resolve, { once: true });
          })
        );
      }
    });

    function afterReady() {
      if (self.destroyed) return;
      requestAnimationFrame(function () {
        requestAnimationFrame(function () {
          self.boot();
        });
      });
    }

    if (waits.length) Promise.all(waits).then(afterReady);
    else afterReady();

    window.addEventListener('load', function () {
      if (!self.destroyed) {
        self.layout();
        self.snap(self.index);
      }
    });
  };

  function init(scope) {
    var roots = (scope || document).querySelectorAll('[data-editorial-hero]');
    Array.prototype.forEach.call(roots, function (root) {
      if (root.__heroCarousel) {
        root.__heroCarousel.kill();
        root.__heroCarousel = null;
      }
      root.__heroCarousel = new HeroCarousel(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init();
    });
  } else {
    init();
  }

  document.addEventListener('click', function (e) {
    var open = e.target.closest('[data-notify-open]');
    if (!open) return;
    var root = open.closest('[data-notify]');
    if (!root) return;
    e.preventDefault();
    e.stopPropagation();
    var fields = root.querySelector('[data-notify-fields]');
    if (!fields) return;
    fields.hidden = false;
    open.hidden = true;
    var input = fields.querySelector('input[type="email"]');
    if (input) input.focus();
  });

  document.addEventListener('shopify:section:load', function (e) {
    init(e.target);
  });

  document.addEventListener('shopify:section:unload', function (e) {
    var root = e.target.querySelector('[data-editorial-hero]');
    if (root && root.__heroCarousel) {
      root.__heroCarousel.kill();
      root.__heroCarousel = null;
    }
  });

  document.addEventListener('shopify:block:select', function (e) {
    var slide = e.target.closest
      ? e.target.closest('[data-hero-slide]')
      : null;
    if (!slide) {
      slide = e.target.querySelector && e.target.querySelector('[data-hero-slide]');
    }
    if (!slide || slide.hasAttribute('data-hero-clone')) return;
    var root = slide.closest('[data-editorial-hero]');
    if (!root || !root.__heroCarousel) return;
    var idx = root.__heroCarousel.realSlides.indexOf(slide);
    if (idx < 0) return;
    root.__heroCarousel.go(idx, true);
    root.__heroCarousel.pauseUser();
  });
})();
